/**
 * Chat Interface Component
 *
 * Real-time chat interface for conversing with LLM about agent/artifact requirements
 */

import { useState, useEffect, useRef, useMemo } from 'react';
import { Send, Loader2, Bot, User, ArrowRight, AlertTriangle } from 'lucide-react';
import { api, transformApi, readStream } from '@/lib/api';
import { API_BASE_URL } from '@/lib/config';
import { useProjectStore } from '@/stores/projectStore';
import { buildUserHeaders } from '@/stores/userStore';
import { Button } from '../ui/Button';
import { ConfirmDialog } from '../ui/ConfirmDialog';

const ASCII_BUTTON_CLASS =
  'inline-flex items-center justify-center px-2 py-1 text-xs font-mono border border-border/60 rounded-sm bg-transparent text-muted-foreground hover:text-foreground hover:bg-orange-500/10 disabled:opacity-40 disabled:cursor-not-allowed';

const getPlanButtonLabel = (artifactType?: string | null, hasArtifact?: boolean) => {
  if (!hasArtifact) return '[ Generate Agent Plan ]';
  switch (artifactType) {
    case 'ATTRIBUTE':
      return '[ Generate Attribute Plan ]';
    case 'INTEGRATION':
      return '[ Generate Integration Plan ]';
    default:
      return '[ Generate Artifact Plan ]';
  }
};

// Get context-aware label for the plan draft section
const getPlanDraftLabel = (artifactType?: string | null): string => {
  switch (artifactType) {
    case 'SKILL':
      return 'Skill Plan Draft';
    case 'ATTRIBUTE':
      return 'Attribute Plan Draft';
    case 'INTEGRATION':
      return 'Integration Plan Draft';
    case 'SUBFLOW':
      return 'Subflow Plan Draft';
    case 'SUBSKILL':
      return 'Subskill Plan Draft';
    case 'FACET':
      return 'Facet Plan Draft';
    default:
      return 'Plan Draft';
  }
};

// Get friendly type name for messages
const getArtifactTypeName = (artifactType?: string | null): string => {
  switch (artifactType) {
    case 'SKILL':
      return 'skill';
    case 'ATTRIBUTE':
      return 'attribute';
    case 'INTEGRATION':
      return 'integration';
    case 'SUBFLOW':
      return 'subflow';
    case 'SUBSKILL':
      return 'subskill';
    case 'FACET':
      return 'facet';
    default:
      return 'artifact';
  }
};

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  created_at: number;
}

interface Conversation {
  id: string;
  project_id: string;
  artifact_id: string | null;
  context_type: 'agent' | 'artifact';
  status: 'active' | 'completed' | 'archived';
  created_at: number;
  updated_at: number;
}

interface ChatInterfaceProps {
  projectId: string;
  artifactId?: string;
  onConversationComplete?: (messages: Message[]) => void;
  /**
   * When true, shows a CTA to generate/push the plan from the current chat
   * and automatically navigates the user to the Plan tab.
   */
  showPlanShortcut?: boolean;
}

export const ChatInterface: React.FC<ChatInterfaceProps> = ({
  projectId,
  artifactId,
  onConversationComplete,
  showPlanShortcut = false
}) => {
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' | 'warning' } | null>(null);
  const [aiReady, setAiReady] = useState<boolean>(true);
  const [showPlanOverwriteConfirm, setShowPlanOverwriteConfirm] = useState(false);
  const [pendingPlanGeneration, setPendingPlanGeneration] = useState<{ source: 'template' | 'conversation'; promptText: string } | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const project = useProjectStore((state) => state.projects[projectId]);
  const artifact = useProjectStore((state) =>
    artifactId ? state.projects[projectId]?.artifacts?.[artifactId] : null
  );
  const loadArtifacts = useProjectStore((state) => state.loadArtifacts);
  const loadProject = useProjectStore((state) => state.loadProject);
  const updateArtifact = useProjectStore((state) => state.updateArtifact);
  const updateArtifactStage = useProjectStore((state) => state.updateArtifactStage);
  const setActiveStage = useProjectStore((state) => state.setActiveStage);
  const templateFetchInFlight = useRef(false);

  // Derive prompt template from artifact data
  const promptTemplate = useMemo(() => {
    if (!artifact) return '';
    return (
      artifact.stages?.PROMPT?.content?.trim() ||
      artifact.originalFullPrompt?.trim() ||
      artifact.originalUserInstruction?.trim() ||
      ''
    );
  }, [artifact]);
  const templateGeneratedOn = artifact?.promptLastModified || artifact?.projectGenerationDate || null;

  // Build default greeting for new conversations
  const buildDefaultGreeting = () => {
    if (artifactId && artifact) {
      const typeName = getArtifactTypeName(artifact.type);
      const planDraftLabel = getPlanDraftLabel(artifact.type);

      if (promptTemplate) {
        // Has a plan draft - reference it in the greeting
        const article = /^[aeiou]/i.test(planDraftLabel) ? 'an' : 'a';
        return `Hello! I see you have ${article} **${planDraftLabel}** for **${artifact.name}**. I can help you:\n\n` +
          `• **Review and refine** the draft — click "Edit Draft" to load it into the chat\n` +
          `• **Generate the plan** directly from the draft — click "Generate Plan"\n` +
          `• **Start fresh** with new requirements — just describe what you need\n\n` +
          `What would you like to do?`;
      }

      // No plan draft yet
      return `Hello! I'm here to help you design the **${artifact.name}** ${typeName}. ` +
        `Describe what you'd like this ${typeName} to do, and I'll help you work through the details and create a plan.`;
    }
    return `Hello! I'm here to help you plan your skill. Describe what you'd like to build and I'll help you work through the details.`;
  };

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Load conversation on mount and when artifact changes
  useEffect(() => {
    loadConversation();
  }, [projectId, artifactId]);

  // Ensure artifact metadata includes prompt template
  useEffect(() => {
    if (!artifactId) return;

    const hasTemplate = Boolean(artifact?.stages?.PROMPT?.content?.trim());

    if (hasTemplate) {
      templateFetchInFlight.current = false;
      return;
    }

    if (templateFetchInFlight.current) return;

    templateFetchInFlight.current = true;
    loadArtifacts(projectId)
      .catch((error) => {
        console.error('[ChatInterface] Failed to refresh prompt template', error);
      })
      .finally(() => {
        templateFetchInFlight.current = false;
      });
  }, [artifact?.stages?.PROMPT?.content, artifactId, projectId, loadArtifacts]);

  // Focus input when loaded
  useEffect(() => {
    if (conversation && !isLoading) {
      inputRef.current?.focus();
    }
  }, [conversation, isLoading]);

  // Auto-dismiss toast after 5 seconds
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const showToast = (message: string, type: 'success' | 'error' | 'info' | 'warning') => {
    setToast({ message, type });
  };

  const _doGenerateArtifactPlan = async (source: 'template' | 'conversation', promptText: string) => {
    if (!artifactId) {
      showToast('Select an artifact before generating a plan.', 'error');
      return;
    }

    const trimmed = promptText.trim();
    if (!trimmed) {
      showToast('Provide prompt details before generating a plan.', 'warning');
      return;
    }

    setIsLoading(true);

    try {
      const stream = await transformApi.promptToPlan({
        prompt: trimmed,
        context: {
          artifactType: artifact?.type ?? 'ARTIFACT',
          artifactName: artifact?.name ?? 'Artifact',
          artifactIdentifier: artifact?.identifier
        }
      });

      let plan = '';
      for await (const chunk of readStream(stream)) {
        plan += chunk;
      }

      await updateArtifactStage(projectId, artifactId, 'ARTIFACT_PLAN', plan, false);
      await updateArtifact(projectId, artifactId, { seedPlan: plan });
      await loadArtifacts(projectId);
      setActiveStage('ARTIFACT_PLAN');

      const noun =
        artifact?.type === 'ATTRIBUTE'
          ? 'Attribute'
          : artifact?.type === 'INTEGRATION'
            ? 'Integration'
            : 'Artifact';
      showToast(`${noun} plan generated from the ${source}.`, 'success');
    } catch (error) {
      console.error('[ChatInterface] Failed to generate artifact plan:', error);
      showToast('Failed to generate artifact plan. Please try again.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const generateArtifactPlan = async (source: 'template' | 'conversation', promptText: string) => {
    // Check if an existing plan needs to be overwritten
    if (artifact?.stages?.ARTIFACT_PLAN?.content) {
      setPendingPlanGeneration({ source, promptText });
      setShowPlanOverwriteConfirm(true);
      return;
    }
    // No existing plan, proceed directly
    await _doGenerateArtifactPlan(source, promptText);
  };


  const loadConversation = async () => {
    setIsLoading(true);
    try {
      const params = artifactId ? `?artifactId=${artifactId}` : '';
      const response = await api.get(`/chat/conversations/${projectId}${params}`);
      const serverMessages: Message[] = response.data.messages || [];
      const hydratedMessages =
        serverMessages.length === 0
          ? [
              {
                id: 'seed-assistant',
                role: 'assistant' as const,
                content: buildDefaultGreeting(),
                created_at: Date.now()
              }
            ]
          : serverMessages.map((msg: any) => ({
              ...msg,
              role: msg.role as 'user' | 'assistant'
            }));
      setConversation(response.data.conversation);
      setMessages(hydratedMessages);
    } catch (error) {
      console.error('Failed to load conversation:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInsertTemplate = () => {
    if (!promptTemplate) return;
    setInput(promptTemplate);
    inputRef.current?.focus();
    showToast('Plan draft loaded into chat. Ask the AI assistant how you would like it refined.', 'info');
  };

  const handleClearTemplate = async () => {
    if (!artifactId || !promptTemplate) return;
    const draftLabel = getPlanDraftLabel(artifact?.type).toLowerCase();
    const confirmed = window.confirm(`Clear the current ${draftLabel}? This cannot be undone.`);
    if (!confirmed) return;
    try {
      await updateArtifactStage(projectId, artifactId, 'PROMPT', '', true);
      await loadArtifacts(projectId);
      setMessages([]);
      setInput('');
      showToast('Plan draft cleared. Start fresh by describing your requirements.', 'info');
    } catch (error) {
      console.error('Failed to clear plan draft:', error);
      showToast('Failed to clear plan draft. Please try again.', 'error');
    }
  };

  const handleGenerateFromTemplate = () => {
    if (!promptTemplate) {
      showToast('Plan draft is empty. Add details before generating.', 'warning');
      return;
    }

    if (artifactId) {
      void generateArtifactPlan('template', promptTemplate);
    } else {
      handleCompleteConversation(promptTemplate);
    }
  };

  const handleSendMessage = async () => {
    if (!input.trim() || !conversation || isStreaming) return;

    const userMessage = input.trim();
    setInput('');
    setIsStreaming(true);

    // Add user message to UI immediately
    const tempUserMessage: Message = {
      id: `temp-${Date.now()}`,
      role: 'user',
      content: userMessage,
      created_at: Date.now()
    };
    setMessages(prev => [...prev, tempUserMessage]);

    // Add placeholder for assistant message
    const tempAssistantMessage: Message = {
      id: `temp-assistant-${Date.now()}`,
      role: 'assistant',
      content: '',
      created_at: Date.now()
    };
    setMessages(prev => [...prev, tempAssistantMessage]);

    try {
      const response = await fetch(
        `${API_BASE_URL}/chat/conversations/${conversation.id}/messages`,
        {
          method: 'POST',
          headers: buildUserHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify({ content: userMessage })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to send message');
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No response reader');
      }

      const decoder = new TextDecoder();
      let assistantContent = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        // Vercel AI SDK toTextStreamResponse() returns plain text chunks
        const chunk = decoder.decode(value, { stream: true });

        if (chunk) {
          assistantContent += chunk;
          setMessages(prev => {
            const newMessages = [...prev];
            const lastMessage = newMessages[newMessages.length - 1];
            if (lastMessage.role === 'assistant') {
              lastMessage.content = assistantContent;
            }
            return newMessages;
          });
        }
      }
      if (!assistantContent) {
        throw new Error('No response from AI service');
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      // Remove both temp messages on error
      setMessages(prev => prev.slice(0, -2));
      
      const errorMessage = error instanceof Error && error.message === 'No response from AI service'
        ? 'AI service returned no response. Please check your API configuration.'
        : 'Failed to send message. Please try again.';
        
      showToast(errorMessage, 'error');
    } finally {
      setIsStreaming(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleCompleteConversation = async (promptOverride?: string) => {
    if (!conversation) return;

    setIsLoading(true);

    try {
      if (artifactId) {
        const conversationPrompt =
          (promptOverride && promptOverride.trim()) ||
          messages
            .filter((message) => message.role === 'user')
            .map((message) => message.content.trim())
            .join('\n\n');

        if (!conversationPrompt.trim()) {
          showToast('Add conversation details before generating a plan.', 'warning');
          return;
        }

        await generateArtifactPlan('conversation', conversationPrompt);
        return;
      }

      // Step 1: Mark conversation as completed
      await api.patch(`/chat/conversations/${conversation.id}`, {
        status: 'completed'
      });

      // Step 2: Generate plan and extract artifacts
      const response = await api.post(
        `/chat/conversations/${conversation.id}/generate-plan`,
        promptOverride ? { promptOverride } : {}
      );

      const extracted = response.data.extractedArtifacts || {};
      
      // Create artifacts in the store
      const createArtifact = useProjectStore.getState().createArtifact;
      const normalizeIdentifier = (name: string) => 
        name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

      const artifactPromises = [];

      // Create Skills
      if (Array.isArray(extracted.skills)) {
        for (const skill of extracted.skills) {
          console.log('[ChatInterface] Extracted Skill:', skill.name);
          artifactPromises.push(
            createArtifact(
              projectId,
              'SKILL',
              normalizeIdentifier(skill.name),
              skill.name,
              skill.description,
              undefined,
              { generatedFromProject: true }
            )
          );
        }
      }

      // Create Attributes
      if (Array.isArray(extracted.attributes)) {
        for (const attr of extracted.attributes) {
          console.log('[ChatInterface] Extracted Attribute:', attr.name);
          artifactPromises.push(
            createArtifact(
              projectId,
              'ATTRIBUTE',
              normalizeIdentifier(attr.name),
              attr.name,
              attr.description,
              undefined,
              { generatedFromProject: true }
            )
          );
        }
      }

      // Create Integrations
      if (Array.isArray(extracted.integrations)) {
        for (const integration of extracted.integrations) {
          console.log('[ChatInterface] Extracted Integration:', integration.name);
          artifactPromises.push(
            createArtifact(
              projectId,
              'INTEGRATION',
              normalizeIdentifier(integration.name),
              integration.name,
              integration.description,
              undefined,
              { generatedFromProject: true }
            )
          );
        }
      }

      await Promise.all(artifactPromises);

      const generatedArtifactCount = artifactPromises.length;

      // Step 3: Call the completion callback
      if (onConversationComplete) {
        onConversationComplete(messages);
      }

      // Show success message
      showToast(`Plan generated successfully. Identified ${generatedArtifactCount} potential artifacts to refine.`, 'success');
      try {
        await loadProject(projectId);
        setActiveStage('PLAN');
      } catch (err) {
        console.warn('[ChatInterface] Failed to refresh project after plan generation', err);
      }
    } catch (error) {
      console.error('Failed to complete conversation:', error);
      showToast('Failed to generate plan. Please try again.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="h-full w-full flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="h-full w-full flex flex-col bg-background">
      {/* AI Readiness Warning */}
      {!aiReady && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border-b border-yellow-200 dark:border-yellow-800 p-3 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-500 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
              AI service is not configured
            </p>
            <p className="text-xs text-yellow-700 dark:text-yellow-300">
              Please configure a valid OpenAI API Key in Admin Settings to use the assistant.
            </p>
          </div>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {promptTemplate && (
          <div className="flex flex-col gap-3 rounded-md border border-border bg-muted/40 p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h4 className="text-sm font-medium text-foreground">{getPlanDraftLabel(artifact?.type)}</h4>
                {templateGeneratedOn && (
                  <p className="text-xs text-muted-foreground">
                    Generated on{' '}
                    {new Date(templateGeneratedOn).toLocaleString(undefined, {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={handleClearTemplate}
                  className={ASCII_BUTTON_CLASS}
                >
                  [ Clear Draft ]
                </button>
                <button
                  type="button"
                  onClick={handleInsertTemplate}
                  className={ASCII_BUTTON_CLASS}
                >
                  [ Edit Draft ]
                </button>
                <button
                  type="button"
                  onClick={handleGenerateFromTemplate}
                  className={ASCII_BUTTON_CLASS}
                >
                  {getPlanButtonLabel(artifact?.type, Boolean(artifactId))}
                </button>
              </div>
            </div>
            <pre className="whitespace-pre-wrap text-xs leading-5 text-foreground/90 font-mono max-h-64 overflow-auto">
              {promptTemplate}
            </pre>
            <p className="text-xs text-muted-foreground">
              Load the draft into chat to refine with the AI assistant, or generate the {getArtifactTypeName(artifact?.type)} plan directly.
            </p>
          </div>
        )}

        {messages.length === 0 ? (
          <div className="h-full flex items-center justify-center text-center text-muted-foreground">
            <div>
              <Bot className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p className="text-sm">Start a conversation to refine your requirements</p>
              <p className="text-xs mt-2">
                Ask questions, clarify details, and explore ideas before generating
              </p>
            </div>
          </div>
        ) : (
          <>
            {messages.map((message, index) => (
              <div
                key={message.id}
                className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                {message.role === 'assistant' && (
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-orange-500/10 flex items-center justify-center">
                    <Bot className="w-5 h-5 text-orange-500" />
                  </div>
                )}
                <div
                  className={`max-w-[70%] rounded-lg px-4 py-2 ${
                    message.role === 'user'
                      ? 'bg-orange-500 text-white'
                      : 'bg-muted border border-border text-foreground'
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                  {message.content === '' && message.role === 'assistant' && (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  )}
                </div>
                {message.role === 'user' && (
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-500/10 flex items-center justify-center">
                    <User className="w-5 h-5 text-blue-500" />
                  </div>
                )}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input Area */}
      <div className="border-t border-border p-4">
        <div className="flex gap-2">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type your message... (Shift+Enter for new line)"
            className="flex-1 px-3 py-2 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none text-sm min-h-[80px] max-h-[200px]"
            disabled={isStreaming || !aiReady}
          />
          <div className="flex flex-col gap-2 min-w-[120px]">
            <Button
              onClick={handleSendMessage}
              disabled={!input.trim() || isStreaming || !aiReady}
              size="sm"
              className="h-full"
            >
              {isStreaming ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
              <Send className="w-4 h-4" />
              )}
            </Button>
            {showPlanShortcut && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  // If no user messages but we have a plan draft, use the draft
                  const userMessages = messages.filter(m => m.role === 'user');
                  if (userMessages.length === 0 && promptTemplate) {
                    void handleCompleteConversation(promptTemplate);
                  } else {
                    void handleCompleteConversation();
                  }
                }}
                disabled={isStreaming || isLoading || (messages.length === 0 && !promptTemplate)}
                className="whitespace-nowrap"
              >
                Generate Plan
              </Button>
            )}
          </div>
        </div>

      </div>

      {/* Toast Notification */}
      {toast && (
        <div
          className={`fixed bottom-4 right-4 px-6 py-3 rounded-lg shadow-lg flex items-center gap-2 animate-in slide-in-from-bottom-5 ${
            toast.type === 'success'
              ? 'bg-green-500 text-white'
              : 'bg-red-500 text-white'
          }`}
        >
          <span className="text-sm font-medium">{toast.message}</span>
          <button
            onClick={() => setToast(null)}
            className="ml-2 text-white hover:text-gray-200"
          >
            ×
          </button>
        </div>
      )}

      <ConfirmDialog
        isOpen={showPlanOverwriteConfirm}
        title="Overwrite Existing Plan?"
        message="An artifact plan already exists. Overwrite it with a new one?"
        confirmLabel="Overwrite"
        cancelLabel="Cancel"
        onConfirm={() => {
          setShowPlanOverwriteConfirm(false);
          if (pendingPlanGeneration) {
            void _doGenerateArtifactPlan(pendingPlanGeneration.source, pendingPlanGeneration.promptText);
          }
          setPendingPlanGeneration(null);
        }}
        onCancel={() => {
          setShowPlanOverwriteConfirm(false);
          setPendingPlanGeneration(null);
        }}
      />
    </div>
  );
};
